/**
 * Scenes — hand-drawn SVG comic scenes for the game's big moments,
 * plus the sleepy mascot and a one-shot confetti burst.
 *
 * All animation is CSS-class driven (see index.css `met-*` keyframes) and
 * fully disabled under prefers-reduced-motion.
 */

import { useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Mascot: Luna, the permanently exhausted moon
// ---------------------------------------------------------------------------

export function MascotMoon({ className, sunglasses = false }: { className?: string; sunglasses?: boolean }) {
  return (
    <svg viewBox="0 0 120 120" className={className} role="img" aria-label="Luna the sleepy moon mascot">
      {/* Glow */}
      <circle cx="60" cy="62" r="52" fill="#fbbf24" opacity="0.12" />
      {/* Crescent body */}
      <path d="M 78 22 A 44 44 0 1 0 78 102 A 36 36 0 1 1 78 22 Z" fill="#fde68a" stroke="#d4af37" strokeWidth="2" />
      {/* Nightcap */}
      <path d="M 40 26 Q 58 -2 88 16 Q 74 20 70 34 Z" fill="#dc2626" />
      <circle cx="90" cy="17" r="5" fill="#f8fafc" stroke="#dc2626" strokeWidth="2" />
      {/* Face */}
      {sunglasses ? (
        <g>
          <rect x="42" y="46" width="18" height="10" rx="3" fill="#0f172a" />
          <rect x="64" y="46" width="18" height="10" rx="3" fill="#0f172a" />
          <line x1="60" y1="50" x2="64" y2="50" stroke="#0f172a" strokeWidth="2.5" />
          <line x1="82" y1="50" x2="88" y2="48" stroke="#0f172a" strokeWidth="2.5" />
        </g>
      ) : (
        <g stroke="#92400e" strokeWidth="2.6" strokeLinecap="round" fill="none">
          {/* Content closed eyes: ∩ ∩ */}
          <path d="M 44 54 Q 50 48 56 54" />
          <path d="M 66 54 Q 72 48 78 54" />
        </g>
      )}
      <path d="M 56 72 Q 62 77 68 72" stroke="#92400e" strokeWidth="2.4" strokeLinecap="round" fill="none" />
      <circle cx="42" cy="66" r="3.4" fill="#fb7185" opacity="0.55" />
    </svg>
  );
}

/** Three floating Z's to sprinkle near anything sleeping. */
export function Zzz({ x = 0, y = 0 }: { x?: number; y?: number }) {
  return (
    <g transform={`translate(${x} ${y})`} fill="#93c5fd" fontFamily="inherit" fontWeight="700">
      <text className="met-zzz" style={{ animationDelay: '0s' }} fontSize="14" x="0" y="0">z</text>
      <text className="met-zzz" style={{ animationDelay: '0.85s' }} fontSize="18" x="10" y="-8">Z</text>
      <text className="met-zzz" style={{ animationDelay: '1.7s' }} fontSize="23" x="24" y="-18">Z</text>
    </g>
  );
}

// ---------------------------------------------------------------------------
// LOSS: the going-out-of-business scene
// ---------------------------------------------------------------------------

export function BankruptcyScene({ className }: { className?: string }) {
  const rainDrops = useMemo(
    () => Array.from({ length: 14 }, (_, i) => ({ x: 12 + i * 22 + (i % 3) * 5, delay: `${(i % 7) * 0.09}s`, len: 7 + (i % 4) * 3 })),
    [],
  );
  return (
    <svg viewBox="0 0 320 180" className={className} role="img" aria-label="A sad going-out-of-business scene">
      {/* Night sky + rain */}
      <rect width="320" height="180" fill="#334155" />
      <g stroke="#94a3b8" strokeWidth="1.4" opacity="0.65">
        {rainDrops.map((d, i) => (
          <line key={i} className="met-rain" style={{ animationDelay: d.delay }} x1={d.x} y1={-6} x2={d.x - 3} y2={-6 + d.len} />
        ))}
      </g>

      {/* Storefront */}
      <rect x="28" y="38" width="150" height="96" fill="#1e293b" stroke="#0f172a" strokeWidth="2" />
      <text x="103" y="30" textAnchor="middle" fontSize="13" fontWeight="700" fill="#e2e8f0" fontFamily="inherit">MATTRESS KING</text>
      <rect x="40" y="60" width="126" height="46" fill="#0b1220" />
      {/* Boarded window */}
      <g stroke="#78350f" strokeWidth="5">
        <line x1="46" y1="64" x2="160" y2="100" />
        <line x1="160" y1="64" x2="46" y2="100" />
      </g>
      {/* Crooked sign */}
      <g transform="rotate(-5 190 52)">
        <rect x="146" y="40" width="92" height="24" rx="3" fill="#fca5a5" stroke="#7f1d1d" strokeWidth="2" />
        <text x="192" y="56" textAnchor="middle" fontSize="10.5" fontWeight="800" fill="#450a0a" fontFamily="inherit">OUT OF BUSINESS</text>
      </g>
      {/* Banner */}
      <g transform="rotate(2 103 118)">
        <rect x="42" y="112" width="122" height="14" fill="#fbbf24" />
        <text x="103" y="123" textAnchor="middle" fontSize="9" fontWeight="700" fill="#450a0a" fontFamily="inherit">EVERYTHING MUST GO (PLEASE)</text>
      </g>

      {/* Sidewalk / curb */}
      <rect x="0" y="134" width="320" height="46" fill="#475569" />
      <line x1="0" y1="134" x2="320" y2="134" stroke="#64748b" strokeWidth="2" />

      {/* Leaning mattress with price tag */}
      <g transform="rotate(-8 232 128)">
        <rect x="196" y="84" width="76" height="46" rx="7" fill="#94a3b8" stroke="#475569" strokeWidth="2" />
        <line x1="200" y1="96" x2="268" y2="96" stroke="#cbd5e1" strokeWidth="2.5" strokeDasharray="5 4" />
        <line x1="200" y1="108" x2="268" y2="108" stroke="#cbd5e1" strokeWidth="2.5" strokeDasharray="5 4" />
      </g>
      <g transform="rotate(12 250 92)">
        <rect x="240" y="80" width="30" height="16" rx="2" fill="#fef9c3" stroke="#a16207" />
        <text x="255" y="92" textAnchor="middle" fontSize="9" fontWeight="700" fill="#713f12" fontFamily="inherit">$19</text>
      </g>

      {/* Founder sitting on the curb, defeated */}
      <g>
        <rect x="86" y="142" width="44" height="12" rx="4" fill="#7c2d12" />
        <circle cx="104" cy="124" r="9" fill="#fcd9b8" />
        <path d="M 96 132 Q 104 138 114 136 L 116 152 L 96 152 Z" fill="#0f172a" />
        <path d="M 112 138 L 124 148" stroke="#0f172a" strokeWidth="4" strokeLinecap="round" />
        {/* Tipped coffee cup */}
        <g transform="rotate(75 130 156)">
          <rect x="124" y="150" width="9" height="12" rx="1.5" fill="#f8fafc" stroke="#94a3b8" />
        </g>
        {/* Single dramatic tear */}
        <circle cx="107" cy="127" r="1.6" fill="#38bdf8" />
      </g>

      {/* Moving boxes */}
      <g>
        <rect x="20" y="146" width="30" height="24" fill="#92400e" stroke="#451a03" strokeWidth="1.5" />
        <rect x="34" y="158" width="26" height="12" fill="#a16207" stroke="#451a03" strokeWidth="1.5" />
        <text x="35" y="162" textAnchor="middle" fontSize="6" fill="#fef3c7" fontFamily="inherit">OUR STUFF</text>
      </g>

      {/* Unimpressed pigeon on the sign */}
      <g transform="translate(206 32)">
        <ellipse cx="0" cy="0" rx="7" ry="5" fill="#94a3b8" />
        <circle cx="6" cy="-5" r="3" fill="#64748b" />
        <path d="M 9 -5 l 4 1.5 l -4 1.5 z" fill="#f59e0b" />
        <line x1="-1" y1="5" x2="-1" y2="8" stroke="#f59e0b" strokeWidth="1.4" />
      </g>

      {/* Sad moon watching from above */}
      <g transform="translate(282 26) scale(0.42)">
        <path d="M 78 22 A 44 44 0 1 0 78 102 A 36 36 0 1 1 78 22 Z" fill="#e2e8f0" opacity="0.9" />
        <path d="M 44 54 Q 50 60 56 54" stroke="#475569" strokeWidth="4" strokeLinecap="round" fill="none" transform="scale(1,-1) translate(0,-108)" />
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// WIN: the acquisition beach scene
// ---------------------------------------------------------------------------

export function BeachExitScene({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 320 180" className={className} role="img" aria-label="A triumphant beach vacation scene">
      <defs>
        <linearGradient id="met-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0ea5e9" />
          <stop offset="100%" stopColor="#bae6fd" />
        </linearGradient>
      </defs>

      {/* Sky + sun + smug moon mascot */}
      <rect width="320" height="110" fill="url(#met-sky)" />
      <circle cx="272" cy="34" r="16" fill="#fde047" />
      <g stroke="#facc15" strokeWidth="2.5" strokeLinecap="round">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <line key={deg} x1={272 + Math.cos((deg * Math.PI) / 180) * 21} y1={34 + Math.sin((deg * Math.PI) / 180) * 21} x2={272 + Math.cos((deg * Math.PI) / 180) * 27} y2={34 + Math.sin((deg * Math.PI) / 180) * 27} />
        ))}
      </g>
      <g transform="translate(20 8) scale(0.5)">
        <MascotMoonContent sunglasses />
      </g>
      <Zzz x={64} y={44} />

      {/* Sea */}
      <rect y="98" width="320" height="26" fill="#0284c7" />
      <g stroke="#7dd3fc" strokeWidth="1.6" strokeLinecap="round">
        <line x1="16" y1="106" x2="52" y2="106" /><line x1="140" y1="110" x2="186" y2="110" />
        <line x1="230" y1="105" x2="268" y2="105" /><line x1="70" y1="115" x2="120" y2="115" />
      </g>

      {/* Sand */}
      <path d="M 0 124 Q 160 112 320 126 L 320 180 L 0 180 Z" fill="#fcd34d" />

      {/* Palm left with sway, palm right */}
      <g className="met-sway">
        <path d="M 52 150 Q 58 110 50 78" stroke="#92400e" strokeWidth="7" fill="none" strokeLinecap="round" />
        <g fill="#16a34a">
          <path d="M 50 78 Q 26 66 12 78 Q 32 80 50 82 Z" />
          <path d="M 50 78 Q 74 64 92 76 Q 70 82 50 82 Z" />
          <path d="M 50 78 Q 40 54 22 50 Q 40 66 48 80 Z" />
          <path d="M 50 78 Q 64 52 84 50 Q 64 68 54 80 Z" />
        </g>
        <circle cx="47" cy="80" r="3" fill="#a16207" /><circle cx="55" cy="81" r="3" fill="#a16207" />
      </g>
      <g transform="translate(258 0) scale(-1 1)">
        <path d="M 52 150 Q 58 112 50 84" stroke="#92400e" strokeWidth="7" fill="none" strokeLinecap="round" />
        <g fill="#16a34a">
          <path d="M 50 84 Q 28 72 14 84 Q 34 86 50 88 Z" />
          <path d="M 50 84 Q 72 70 90 82 Q 68 88 50 88 Z" />
          <path d="M 50 84 Q 42 60 26 56 Q 42 72 48 86 Z" />
        </g>
      </g>

      {/* The mattress, relaxing in a hammock between palms */}
      <path d="M 56 96 Q 155 132 246 98" stroke="#7c2d12" strokeWidth="2.4" fill="none" />
      <g transform="rotate(-3 151 112)">
        <rect x="92" y="100" width="118" height="26" rx="9" fill="#f8fafc" stroke="#334155" strokeWidth="2" />
        {[0, 1, 2, 3].map((i) => (
          <rect key={i} x={100 + i * 27} y="101" width="13" height="24" fill="#ef4444" opacity="0.75" />
        ))}
      </g>

      {/* Founder lounging on it with a drink */}
      <g>
        <circle cx="128" cy="94" r="8.5" fill="#fcd9b8" />
        <path d="M 121 87 Q 128 80 137 86 L 133 92 Z" fill="#fbbf24" />
        <path d="M 119 102 Q 135 96 152 102 L 149 114 L 122 114 Z" fill="#0ea5e9" />
        <path d="M 150 104 L 166 100" stroke="#fcd9b8" strokeWidth="4.5" strokeLinecap="round" />
        <g transform="rotate(-14 170 94)">
          <rect x="166" y="88" width="7" height="13" rx="1.5" fill="#f87171" opacity="0.9" />
          <line x1="169" y1="82" x2="173" y2="89" stroke="#22c55e" strokeWidth="2" />
          <circle cx="174" cy="81" r="1.8" fill="#f87171" />
        </g>
        <path d="M 121 113 L 117 122 M 145 114 L 149 122" stroke="#fcd9b8" strokeWidth="4" strokeLinecap="round" />
      </g>

      {/* Giant novelty check */}
      <g transform="rotate(-4 60 148)">
        <rect x="14" y="128" width="96" height="44" rx="3" fill="#fffbeb" stroke="#d4af37" strokeWidth="2.5" />
        <text x="62" y="143" textAnchor="middle" fontSize="7.5" fill="#713f12" fontFamily="inherit">PAY TO THE ORDER OF YOU</text>
        <text x="62" y="159" textAnchor="middle" fontSize="15" fontWeight="800" fill="#166534" fontFamily="inherit">$BIG,LY</text>
        <path d="M 30 165 q 10 -6 22 0 q 10 5 22 -2" stroke="#0f172a" strokeWidth="1.6" fill="none" />
      </g>

      {/* Flying money */}
      <g>
        {[
          { x: 210, y: 60, d: '0s' }, { x: 244, y: 84, d: '.7s' }, { x: 188, y: 40, d: '1.3s' },
        ].map((m, i) => (
          <g key={i} className="met-bill" style={{ animationDelay: m.d }} transform={`translate(${m.x} ${m.y})`}>
            <rect x="-9" y="-5" width="18" height="10" rx="1.5" fill="#4ade80" stroke="#166534" strokeWidth="1.2" />
            <circle cx="0" cy="0" r="2.6" fill="#bbf7d0" />
          </g>
        ))}
      </g>

      {/* Signpost */}
      <g transform="translate(296 128)">
        <rect x="-3" y="0" width="6" height="42" fill="#78350f" />
        <rect x="-26" y="-14" width="52" height="15" rx="2" fill="#fef9c3" stroke="#a16207" strokeWidth="1.5" />
        <text x="0" y="-3" textAnchor="middle" fontSize="8" fontWeight="700" fill="#713f12" fontFamily="inherit">GONE SLEEPING</text>
      </g>
    </svg>
  );
}

/** Mascot internals reused inside scenes without an extra <svg> wrapper. */
function MascotMoonContent({ sunglasses = false }: { sunglasses?: boolean }) {
  return (
    <g>
      <path d="M 78 22 A 44 44 0 1 0 78 102 A 36 36 0 1 1 78 22 Z" fill="#fde68a" stroke="#d4af37" strokeWidth="2" />
      <path d="M 40 26 Q 58 -2 88 16 Q 74 20 70 34 Z" fill="#dc2626" />
      <circle cx="90" cy="17" r="5" fill="#f8fafc" stroke="#dc2626" strokeWidth="2" />
      {sunglasses ? (
        <g fill="#0f172a">
          <rect x="42" y="46" width="18" height="10" rx="3" />
          <rect x="64" y="46" width="18" height="10" rx="3" />
        </g>
      ) : null}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Confetti burst (one-shot per trigger increment)
// ---------------------------------------------------------------------------

const CONFETTI_COLORS = ['#38bdf8', '#4ade80', '#fbbf24', '#fb7185', '#c084fc', '#f8fafc'];

export function Confetti({ burst }: { burst: number }) {
  const [visible, setVisible] = useState(false);

  const pieces = useMemo(() => {
    if (burst <= 0) return [];
    return Array.from({ length: 64 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      dur: 1.5 + Math.random() * 1.2,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      w: 6 + Math.random() * 7,
      rot: Math.floor(Math.random() * 360),
    }));
  }, [burst]);

  useEffect(() => {
    if (burst <= 0) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 3200);
    return () => clearTimeout(t);
  }, [burst]);

  if (!visible || pieces.length === 0) return null;
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-[95] overflow-hidden">
      {pieces.map((p) => (
        <span
          key={`${burst}-${p.id}`}
          className="met-confetti-piece"
          style={{
            left: `${p.left}%`,
            width: p.w,
            height: p.w * 1.45,
            background: p.color,
            animationDuration: `${p.dur}s`,
            animationDelay: `${p.delay}s`,
            rotate: `${p.rot}deg`,
          }}
        />
      ))}
    </div>
  );
}
